import type { MailboxFolder } from "@/models/MailboxConnection";

type Translate = (key: string, values?: Record<string, unknown>) => string;

export interface FolderNode {
  id: string;
  label: string;
  folder: MailboxFolder;
  /** el-tree disables the checkbox of a folder that is never synced. */
  disabled: boolean;
  children: FolderNode[];
}

/**
 * English well-known names a provider reports for a role (Gmail system labels, Graph and IMAP
 * defaults). Only such a folder gets the translated role name; a folder the user named
 * ("Archive 2023") keeps its own name even when the backend gave it a role.
 */
const WELL_KNOWN_NAMES: Record<string, string[]> = {
  inbox: ["inbox"],
  sent: ["sent", "sent items", "sent mail", "sent messages"],
  drafts: ["draft", "drafts"],
  junk: ["junk", "junk email", "junk e-mail", "spam"],
  trash: ["trash", "deleted items", "deleted messages", "bin"],
  archive: ["archive", "archives"],
  outbox: ["outbox"],
};

/** Order of the roles within a level; every other selectable folder follows alphabetically. */
const ROLE_RANK: Record<string, number> = { inbox: 0, sent: 1, archive: 2 };
const OTHER_RANK = 3;
const EXCLUDED_RANK = 4;

/** Display name: the translated role name for a well-known English name, otherwise the folder's name. */
export function folderLabel(folder: Pick<MailboxFolder, "name" | "role">, t: Translate): string {
  const name = (folder.name ?? "").trim();
  const known = WELL_KNOWN_NAMES[folder.role];
  if (known && known.includes(name.toLowerCase())) return t("mail.connections.folder_roles." + folder.role);
  return name || "–";
}

function rank(folder: MailboxFolder): number {
  if (!folder.selectable) return EXCLUDED_RANK;
  return ROLE_RANK[folder.role] ?? OTHER_RANK;
}

function compareNodes(a: FolderNode, b: FolderNode): number {
  const byRank = rank(a.folder) - rank(b.folder);
  if (byRank !== 0) return byRank;
  const byLabel = a.label.localeCompare(b.label, "de", { sensitivity: "base", numeric: true });
  return byLabel !== 0 ? byLabel : a.id.localeCompare(b.id);
}

/**
 * Builds the folder hierarchy from the flat list via `parent_id`. A folder whose parent is not in
 * the list (or that would close a cycle) becomes a root. Each level is sorted: inbox, sent,
 * archive, the other folders alphabetically, the never-synced folders last.
 */
export function buildFolderTree(folders: MailboxFolder[], t: Translate): FolderNode[] {
  const nodes = new Map<string, FolderNode>();
  for (const folder of folders) {
    nodes.set(folder.id, { id: folder.id, label: folderLabel(folder, t), folder, disabled: !folder.selectable, children: [] });
  }

  const roots: FolderNode[] = [];
  for (const node of nodes.values()) {
    const parent = parentOf(node, nodes);
    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  const sort = (level: FolderNode[]) => {
    level.sort(compareNodes);
    level.forEach((node) => sort(node.children));
  };
  sort(roots);
  return roots;
}

/** The parent node, or null for a root: missing parent, self-reference or a cycle. */
function parentOf(node: FolderNode, nodes: Map<string, FolderNode>): FolderNode | null {
  const parentId = node.folder.parent_id;
  if (!parentId || parentId === node.id) return null;
  const parent = nodes.get(parentId);
  if (!parent) return null;
  // Walk up from the parent; reaching the node again means it sits on a cycle and becomes a root.
  const seen = new Set<string>();
  let current: FolderNode | undefined = parent;
  while (current && !seen.has(current.id)) {
    if (current.id === node.id) return null;
    seen.add(current.id);
    const next: string | null = current.folder.parent_id;
    current = next ? nodes.get(next) : undefined;
  }
  return parent;
}

/**
 * Ids of the selected folders that can be synced, in catalogue order. A folder that became
 * non-selectable while still selected is left out, so it neither counts as a change nor is sent.
 */
export function selectedFolderIds(folders: MailboxFolder[]): string[] {
  return folders.filter((folder) => folder.selected && folder.selectable).map((folder) => folder.id);
}

/** What a new selection adds and removes compared to the saved one. */
export function diffSelection(before: string[], after: string[]): { added: string[]; removed: string[] } {
  const was = new Set(before);
  const now = new Set(after);
  return {
    added: [...now].filter((id) => !was.has(id)),
    removed: [...was].filter((id) => !now.has(id)),
  };
}

/** True when the selection differs from the saved one (order does not matter). */
export function selectionChanged(before: string[], after: string[]): boolean {
  const { added, removed } = diffSelection(before, after);
  return added.length > 0 || removed.length > 0;
}
